import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiGet } from '../lib/api';
import { useAuth } from './AuthContext';
import type { Traitant, ClientRef } from '../types';
import { initialsOf } from '../lib/people';
import { isDtStaff } from '../lib/staff';

interface RefsCtx {
  traitants: Traitant[];
  clients: ClientRef[];
  traitantById: (id: number) => Traitant | undefined;
  clientById: (id: number) => ClientRef | undefined;
  initials: (id: number) => string;
  reload: () => Promise<void>;
}

const Ctx = createContext<RefsCtx | null>(null);

export function RefsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [traitants, setTraitants] = useState<Traitant[]>([]);
  const [clients, setClients] = useState<ClientRef[]>([]);

  const reload = useCallback(async () => {
    if (!user) {
      setTraitants([]);
      setClients([]);
      return;
    }
    const tasks: Promise<unknown>[] = [];
    // Liste des traitants : réservée par l'API aux agents Digital Team (403 sinon).
    if (isDtStaff(user)) {
      tasks.push(
        apiGet<{ items: Traitant[] }>('/api/refs/traitants')
          .then((r) => setTraitants(r.items))
          .catch(() => {}),
      );
    }
    tasks.push(
      apiGet<{ items: ClientRef[] }>('/api/refs/clients')
        .then((r) => setClients(r.items))
        .catch(() => {}),
    );
    await Promise.all(tasks);
  }, [user]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const traitantById = useCallback((id: number) => traitants.find((t) => t.id_user === id), [traitants]);
  const clientById = useCallback((id: number) => clients.find((c) => c.id_client === id), [clients]);

  const initials = useCallback(
    (id: number) => initialsOf(traitants.find((tr) => tr.id_user === id)?.nom_user, id),
    [traitants],
  );

  return (
    <Ctx.Provider value={{ traitants, clients, traitantById, clientById, initials, reload }}>{children}</Ctx.Provider>
  );
}

export function useRefs(): RefsCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRefs must be used within RefsProvider');
  return v;
}
